import React from 'react'

import { LeftImageContent, NoImageContent, TopImageContent } from '..'
import { SectionProps } from '../../../../core/types/Help'
import { List, SectionTitle, Text } from '../ContentItem'
import { HELP_IMAGES } from '../../../constants/HelpImages'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

const Goals: React.FC<SectionProps> = ({ copy }) => {
  const { goal_widget_overview: goalsContent } = copy

  return (
    <React.Fragment>
      <Text content={goalsContent.goals_overview_title} />

      <NoImageContent
        content={
          <React.Fragment>
            <SectionTitle content={goalsContent.setting_goals_section_title} />
            <Text content={`${goalsContent.setting_goals_title_text}:`} />
            <List
              items={[
                buildDynamicCopy(
                  goalsContent.setting_goals_step1,
                  <b>{goalsContent.savings_bold_text}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  goalsContent.setting_goals_step2,
                  <b>{goalsContent.debt_payoff_bold_text}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  goalsContent.setting_goals_step3,
                  <b>{goalsContent.retirement_bold_text}</b>,
                ) as React.ReactNode,
              ]}
            />
          </React.Fragment>
        }
        hasDivider={true}
      />
      <SectionTitle content={goalsContent.create_goal_section_title} sx={{ pt: 48 }} />
      <TopImageContent
        altImage="create_goal"
        content={
          <List
            items={[
              buildDynamicCopy(
                goalsContent.create_goal_step1,
                <b>{goalsContent.add_goal_bold_text}</b>,
              ) as React.ReactNode,
              buildDynamicCopy(
                goalsContent.create_goal_step2,
                <b>{goalsContent.savings_bold_text}</b>,
                <b>{goalsContent.debt_payoff_bold_text}</b>,
                <b>{goalsContent.retirement_bold_text}</b>,
              ) as React.ReactNode,
              goalsContent.create_goal_step3,
              goalsContent.create_goal_step4,
              buildDynamicCopy(
                goalsContent.create_goal_step5,
                <b>{goalsContent.save_bold_text}</b>,
              ) as React.ReactNode,
            ]}
            listType="ol"
          />
        }
        hasDivider={true}
        image={HELP_IMAGES.GOALS_CREATE_GOAL}
        imageLargeBreakPoint={10}
      />

      <SectionTitle content={goalsContent.savings_goal_section_title} sx={{ pt: 48 }} />
      <LeftImageContent
        altImage="savings_goal"
        content={
          <List
            items={[
              goalsContent.savings_goals_step1,
              goalsContent.savings_goals_step2,
              buildDynamicCopy(
                goalsContent.savings_goals_step3,
                <b>{goalsContent.priority_bold_text}</b>,
                <b>{goalsContent.monthly_contribution_bold_text}</b>,
              ) as React.ReactNode,
            ]}
          />
        }
        hasDivider={true}
        image={HELP_IMAGES.GOALS_SAVINGS_GOALS}
        imageLargeBreakPoint={3}
      />
      <SectionTitle content={goalsContent.debt_payoff_goal_section_title} sx={{ pt: 48 }} />
      <LeftImageContent
        altImage="debt_payoff"
        content={
          <React.Fragment>
            <List
              items={[
                goalsContent.debt_payoff_goals_step1,
                goalsContent.debt_payoff_goals_step2,
                buildDynamicCopy(
                  goalsContent.debt_payoff_goals_step3,
                  <b>{goalsContent.fastest_payoff_bold_text}</b>,
                  <b>{goalsContent.highest_interest_bold_text}</b>,
                  <b>{goalsContent.lowest_balance_bold_text}</b>,
                  <b>{goalsContent.highest_balance_bold_text}</b>,
                ) as React.ReactNode,
              ]}
            />
          </React.Fragment>
        }
        hasDivider={true}
        image={HELP_IMAGES.GOALS_DEBT_GOALS}
        imageLargeBreakPoint={3}
      />
      <SectionTitle content={goalsContent.retirement_goal_section_title} sx={{ pt: 48 }} />
      <LeftImageContent
        altImage="retirement_goal"
        content={
          <List
            items={[
              buildDynamicCopy(
                goalsContent.retirement_goals_step1,
                <b>{goalsContent.desired_retirement_age_bold_text}</b>,
                <b>{goalsContent.savings_goal_bold_text}</b>,
              ) as React.ReactNode,
              goalsContent.retirement_goals_step2,
              buildDynamicCopy(
                goalsContent.retirement_goals_step3,
                <b>{goalsContent.blue_i_icon_bold_text}</b>,
              ) as React.ReactNode,
            ]}
          />
        }
        hasDivider={true}
        image={HELP_IMAGES.GOALS_RETIREMENT_GOALS}
        imageLargeBreakPoint={3}
      />
      <SectionTitle
        content={goalsContent.adjusting_contribution_priorities_section_title}
        sx={{ pt: 48 }}
      />
      <LeftImageContent
        altImage="manage_goal"
        content={
          <List
            items={[
              buildDynamicCopy(
                goalsContent.adjusting_contribution_priorities_step1,
                <b>{goalsContent.manage_goals_bold_text}</b>,
              ) as React.ReactNode,
              buildDynamicCopy(
                goalsContent.create_goal_step2,
                <b>{goalsContent.savings_bold_text}</b>,
                <b>{goalsContent.debt_payoff_bold_text}</b>,
                <b>{goalsContent.retirement_bold_text}</b>,
              ) as React.ReactNode,
              buildDynamicCopy(
                goalsContent.adjusting_contribution_priorities_step3,
                <b>{goalsContent.pencil_icon_bold_text}</b>,
              ) as React.ReactNode,
              goalsContent.adjusting_contribution_priorities_step4,
            ]}
          />
        }
        hasDivider={false}
        image={HELP_IMAGES.GOALS_MANAGE_GOALS}
        imageLargeBreakPoint={3}
      />
    </React.Fragment>
  )
}

export default Goals
